import { useState } from 'react'
import {
  type ApiTokenView,
  type MintedToken,
  scopeGroups,
  tokenActions,
  useAdminApiTokens,
  useCreateApiToken,
  useDeleteApiToken,
  useRevokeApiToken,
  useRotateApiToken,
  useUpdateApiTokenScopes,
} from '../../../features/admin/use-admin-api-tokens'
import { useAdminPermissions } from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError, Meta, rowCls } from './admin-bits'
import { Btn, Chip, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, td, th } from './controls'
import { mono, Panel } from './ui'

function ScopePicker({ selected, onChange }: { selected: string[]; onChange: (next: string[]) => void }) {
  const perms = useAdminPermissions()
  const groups = scopeGroups(perms.data?.permissions ?? [], selected)
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])
  return (
    <div
      className="max-h-56 overflow-auto border border-[var(--color-border)] p-2"
      style={{ borderRadius: 'var(--radius)' }}
    >
      {groups.length === 0 ? <p className="text-[12px] text-[var(--color-muted)]">No scopes available.</p> : null}
      {groups.map((g) => (
        <fieldset key={g.groupTag} className="mb-2 last:mb-0">
          <legend className="mb-0.5 text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
            {g.groupTag}
          </legend>
          {g.scopes.map((s) => (
            <label key={s.id} className="flex h-6 items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--color-accent)]"
                checked={selected.includes(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span style={mono}>{s.name}</span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

/** Shown once, from component state only. Closing discards it. */
function RawTokenDialog({ minted, onClose }: { minted: MintedToken; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog
      open
      title="Copy your token now"
      onClose={onClose}
      footer={
        <Btn variant="primary" onClick={onClose}>
          I have saved it
        </Btn>
      }
    >
      <p className="text-[12px] text-[var(--color-muted)]">
        Shown once for <strong className="text-[var(--color-fg)]">{minted.token.name}</strong>. It cannot be retrieved
        later.
      </p>
      <code
        className="block break-all border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-[12px]"
        style={{ ...mono, borderRadius: 'var(--radius)' }}
      >
        {minted.rawToken}
      </code>
      <div>
        <Btn
          onClick={() => {
            navigator.clipboard
              ?.writeText(minted.rawToken)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Btn>
      </div>
    </Dialog>
  )
}

function CreateDialog({ onClose, onMinted }: { onClose: () => void; onMinted: (m: MintedToken) => void }) {
  const create = useCreateApiToken()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>([])
  const [expires, setExpires] = useState('')
  const valid = name.trim() !== '' && scopes.length > 0
  const submit = () => {
    if (!valid || create.isPending) return
    create.mutate(
      { name: name.trim(), scopes, expiresAt: expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null },
      { onSuccess: onMinted },
    )
  }
  return (
    <Dialog
      open
      title="Create API token"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Btn onClick={onClose} disabled={create.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating...' : 'Create'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Expires" hint="optional">
        <input className={inputCls} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </Field>
      <div>
        <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
          Scopes (at least one)
        </span>
        <ScopePicker selected={scopes} onChange={setScopes} />
      </div>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

function ScopesDialog({ token, onClose }: { token: ApiTokenView; onClose: () => void }) {
  const upd = useUpdateApiTokenScopes()
  const [scopes, setScopes] = useState(token.scopes)
  return (
    <Dialog
      open
      title={`Scopes: ${token.name}`}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={upd.isPending}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            disabled={scopes.length === 0 || upd.isPending}
            onClick={() => upd.mutate({ id: token.id, scopes }, { onSuccess: onClose })}
          >
            {upd.isPending ? 'Saving...' : 'Save scopes'}
          </Btn>
        </>
      }
    >
      <ScopePicker selected={scopes} onChange={setScopes} />
      {upd.error ? <Inline tone="bad">{adminError(upd.error)}</Inline> : null}
    </Dialog>
  )
}

type Pending = { kind: 'rotate' | 'revoke' | 'delete'; token: ApiTokenView }

const statusTone = (s: ApiTokenView['status']) => (s === 'active' ? 'ok' : s === 'expired' ? 'warn' : 'off')
const fmt = (iso: string | null) => (iso ? iso.slice(0, 10) : '-')

const COPY = {
  rotate: ['Rotate token', 'A new secret is issued and the old one stops working immediately.', 'Rotate'],
  revoke: ['Revoke token', 'It stops working immediately. You can delete it afterwards.', 'Revoke'],
  delete: ['Delete token', 'The revoked token record is permanently removed.', 'Delete'],
} as const

export function SignalAdminApiTokens() {
  const tokens = useAdminApiTokens()
  const rotate = useRotateApiToken()
  const revoke = useRevokeApiToken()
  const del = useDeleteApiToken()
  const [creating, setCreating] = useState(false)
  const [minted, setMinted] = useState<MintedToken | null>(null)
  const [scopesFor, setScopesFor] = useState<ApiTokenView | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const list = tokens.data ?? []
  const m = pending?.kind === 'rotate' ? rotate : pending?.kind === 'revoke' ? revoke : del
  const close = () => {
    m.reset()
    setPending(null)
  }
  const confirm = () => {
    if (!pending) return
    const { id } = pending.token
    if (pending.kind === 'rotate')
      rotate.mutate(
        { id },
        {
          onSuccess: (r) => {
            setPending(null)
            setMinted(r)
          },
        },
      )
    else (pending.kind === 'revoke' ? revoke : del).mutate({ id }, { onSuccess: () => setPending(null) })
  }
  return (
    <AdminPage
      page="api-tokens"
      title="API tokens"
      meta={<Meta>{list.length} tokens</Meta>}
      loading={tokens.isLoading}
      error={!!tokens.error}
      right={
        <Btn variant="primary" onClick={() => setCreating(true)}>
          Create token
        </Btn>
      }
    >
      <Panel title="Machine access">
        {list.length === 0 ? (
          <Empty>No API tokens yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Prefix</th>
                  <th className={th}>Scopes</th>
                  <th className={th}>Status</th>
                  <th className={th}>Expires</th>
                  <th className={th}>Last used</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const a = tokenActions(t)
                  return (
                    <tr key={t.id} className={rowCls}>
                      <td className={`${td} font-medium`}>{t.name}</td>
                      <td className={td} style={mono}>
                        {t.prefixDisplay}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                        {t.scopes.length}
                      </td>
                      <td className={td}>
                        <Chip tone={statusTone(t.status)}>{t.status}</Chip>
                      </td>
                      <td className={td} style={mono}>
                        {fmt(t.expiresAt)}
                      </td>
                      <td className={td} style={mono}>
                        {fmt(t.lastSeenAt)}
                      </td>
                      <td className={`${td} text-right`}>
                        <span className="inline-flex gap-1">
                          <Btn disabled={t.status === 'revoked'} onClick={() => setScopesFor(t)}>
                            Scopes
                          </Btn>
                          {a.canRotate ? (
                            <Btn onClick={() => setPending({ kind: 'rotate', token: t })}>Rotate</Btn>
                          ) : null}
                          {a.canRevoke ? (
                            <Btn variant="danger" onClick={() => setPending({ kind: 'revoke', token: t })}>
                              Revoke
                            </Btn>
                          ) : null}
                          {a.canDelete ? (
                            <Btn variant="danger" onClick={() => setPending({ kind: 'delete', token: t })}>
                              Delete
                            </Btn>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onMinted={(r) => {
            setCreating(false)
            setMinted(r)
          }}
        />
      ) : null}
      {scopesFor ? <ScopesDialog token={scopesFor} onClose={() => setScopesFor(null)} /> : null}
      {minted ? <RawTokenDialog minted={minted} onClose={() => setMinted(null)} /> : null}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? COPY[pending.kind][0] : ''}
        body={pending ? `${pending.token.name}: ${COPY[pending.kind][1]}` : null}
        confirmLabel={pending ? COPY[pending.kind][2] : ''}
        danger={pending?.kind !== 'rotate'}
        pending={m.isPending}
        error={m.error ? adminError(m.error) : null}
        onConfirm={confirm}
        onClose={close}
      />
    </AdminPage>
  )
}
